/**
 * Microphone capture for recorded narration (M7).
 *
 * Browser-native throughout: `getUserMedia` + `MediaRecorder`, no native audio
 * layer. `getDisplayMedia` is not permitted inside an Office add-in iframe,
 * which is why screen recording (M10) is a separate and much more constrained
 * problem.
 *
 * `getUserMedia` is permitted, but not unconditionally. In Office on the web the
 * add-in iframe is created without the `microphone` permission-policy feature
 * until the add-in has asked through `Office.devicePermission`; the call then
 * fails with `NotAllowedError` and the browser never prompts. `prepareMicrophone`
 * handles that platform difference, and must be called before `startRecording`.
 * See `packages/host-bridge/src/device-permission.ts`.
 *
 * The level meter is not decoration. Recording narration with a muted or wrong
 * input device produces a silent take that looks completely normal in the UI,
 * and the author only finds out after publishing.
 */

import { isOfficeOnline, requestMicrophonePermission } from '@slideforge/host-bridge';

export interface AudioDevice {
  readonly deviceId: string;
  readonly label: string;
}

export interface RecorderHandle {
  /** Resolves with the recorded audio once `stop()` has been called. */
  readonly done: Promise<Blob>;
  stop(): void;
  cancel(): void;
  /** 0..1, smoothed. Read on an animation frame to drive a meter. */
  level(): number;
  readonly mimeType: string;
}

/**
 * Preference order. Opus in WebM is what the plan specifies; the fallbacks
 * exist because Safari has historically supported neither, and a missing codec
 * must degrade to "records in something" rather than to a thrown error.
 */
const CANDIDATE_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
];

export function recordingSupported(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    navigator.mediaDevices?.getUserMedia !== undefined
  );
}

export function preferredMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return '';
  for (const type of CANDIDATE_TYPES) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }
  return '';
}

/**
 * Lists microphones.
 *
 * Labels are empty until permission has been granted at least once — that is
 * the spec, not a bug — so the caller shows a generic name rather than a blank
 * row, and the list is worth re-reading after the first recording.
 */
export async function listMicrophones(): Promise<AudioDevice[]> {
  if (!navigator.mediaDevices?.enumerateDevices) return [];
  const devices = await navigator.mediaDevices.enumerateDevices();
  return devices
    .filter((device) => device.kind === 'audioinput')
    .map((device, i) => ({
      deviceId: device.deviceId,
      label: device.label || `Microphone ${i + 1}`,
    }));
}

export class MicrophoneError extends Error {
  constructor(
    readonly reason: 'denied' | 'not-found' | 'unsupported' | 'failed' | 'reload-required',
    message: string,
  ) {
    super(message);
    this.name = 'MicrophoneError';
  }
}

/**
 * True when the frame this add-in runs in was created without the `microphone`
 * permission-policy feature. Then no answer to any prompt can help: the browser
 * refuses before the user is ever asked, and the two denials are otherwise
 * indistinguishable — both surface as `NotAllowedError`.
 */
function frameForbidsMicrophone(): boolean {
  try {
    const policy = (
      document as {
        featurePolicy?: { allowsFeature(feature: string): boolean };
        permissionsPolicy?: { allowsFeature(feature: string): boolean };
      }
    );
    const api = policy.permissionsPolicy ?? policy.featurePolicy;
    return api?.allowsFeature('microphone') === false;
  } catch {
    return false;
  }
}

/**
 * What a denial means, and what the author can actually do about it, differs by
 * platform. Telling a desktop user to check a browser site setting they do not
 * have — or a web user to open Windows privacy settings that Office on the web
 * never consults — is worse than saying nothing.
 *
 * `note` carries whatever the Office permission request said, so a failure that
 * began there is not re-attributed to something the author did.
 */
function deniedError(note?: string): MicrophoneError {
  const suffix = note ? ` ${note}` : '';

  if (frameForbidsMicrophone()) {
    return new MicrophoneError(
      'denied',
      'This PowerPoint window gives SlideForge no microphone access at all, so no permission prompt can appear.' +
        suffix +
        ' In PowerPoint on the web this is usually a stale permission record for a sideloaded add-in: clear the site data for this tab (browser settings, or delete the OSF.ADDINS.AddinPermissions IndexedDB database in DevTools), reload the presentation and try again. Otherwise record in PowerPoint for Windows, where SlideForge reaches the microphone directly.',
    );
  }
  if (isOfficeOnline()) {
    return new MicrophoneError(
      'denied',
      'Microphone access was denied.' +
        suffix +
        ' Choose Allow if Office shows a permission dialog. If no dialog appears at all, clear the site data for this tab and reload the presentation: Office keeps its own permission record for the add-in, a sideloaded add-in can lose it, and Office then refuses without asking.',
    );
  }
  return new MicrophoneError(
    'denied',
    'Windows blocked access to the microphone.' +
      suffix +
      ' Open Settings > Privacy & security > Microphone, turn on Microphone access and "Let desktop apps access your microphone", then restart PowerPoint.',
  );
}

export interface MicrophonePreparation {
  /**
   * Office granted the permission just now. It applies to a newly created frame,
   * not this one, so the caller must reload before capture will work.
   */
  readonly reloadRequired: boolean;
  /** What the permission request reported, when it was not a clean grant. */
  readonly note?: string;
}

/**
 * Asks Office for microphone access on the platforms that require it.
 *
 * Call before `startRecording`, and before any countdown: on Office on the web
 * this can show a modal permission dialog, and a countdown running behind a
 * modal is a take that starts mid-word.
 *
 * A failed request is **not** treated as final. Office reports a refusal the
 * same way whether the author pressed Deny or no dialog was ever shown
 * (office-js#5726, #5795), and the frame may already hold the permission from an
 * earlier grant. So the note is carried into the capture attempt and the browser
 * decides. Only a first-time grant stops the flow, because a reload has to
 * happen first.
 */
export async function prepareMicrophone(): Promise<MicrophonePreparation> {
  const { outcome, detail } = await requestMicrophonePermission();
  if (outcome === 'granted-needs-reload') return { reloadRequired: true };
  return { reloadRequired: false, note: detail };
}

/**
 * Turns the browser's DOMException zoo into something an author can act on.
 * "NotAllowedError" is not a sentence anybody outside this file understands.
 */
function describe(error: unknown, note?: string): MicrophoneError {
  const name = error instanceof Error ? error.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return deniedError(note);
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return new MicrophoneError('not-found', 'No microphone was found. Plug one in and try again.');
  }
  return new MicrophoneError(
    'failed',
    error instanceof Error ? error.message : 'The microphone could not be opened.',
  );
}

export async function startRecording(
  deviceId?: string,
  options?: { readonly permissionNote?: string },
): Promise<RecorderHandle> {
  if (!recordingSupported()) {
    throw new MicrophoneError(
      'unsupported',
      'This version of PowerPoint cannot record audio. Recording works in PowerPoint on Windows, on Mac, and on the web.',
    );
  }

  const mimeType = preferredMimeType();
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: deviceId ? { deviceId: { exact: deviceId } } : true,
    });
  } catch (error) {
    throw describe(error, options?.permissionNote);
  }

  // A level meter needs the signal, not the encoded output, so the stream is
  // tapped separately from the recorder.
  const audioContext = new AudioContext();
  const analyser = audioContext.createAnalyser();
  analyser.fftSize = 1024;
  analyser.smoothingTimeConstant = 0.6;
  audioContext.createMediaStreamSource(stream).connect(analyser);
  const samples = new Float32Array(analyser.fftSize);

  const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
  const chunks: Blob[] = [];
  recorder.ondataavailable = (event) => {
    if (event.data.size > 0) chunks.push(event.data);
  };

  let settled = false;
  const teardown = () => {
    for (const track of stream.getTracks()) track.stop();
    void audioContext.close().catch(() => undefined);
  };

  const done = new Promise<Blob>((resolve, reject) => {
    recorder.onstop = () => {
      teardown();
      if (settled) return;
      settled = true;
      resolve(new Blob(chunks, { type: recorder.mimeType || mimeType || 'audio/webm' }));
    };
    recorder.onerror = () => {
      teardown();
      if (settled) return;
      settled = true;
      reject(new MicrophoneError('failed', 'Recording stopped unexpectedly.'));
    };
  });

  // Timeslice so a long recording is not one enormous final blob, and so a
  // crash mid-take still leaves recoverable chunks.
  recorder.start(1000);

  return {
    done,
    mimeType: recorder.mimeType || mimeType || 'audio/webm',
    stop() {
      if (recorder.state !== 'inactive') recorder.stop();
    },
    cancel() {
      settled = true;
      if (recorder.state !== 'inactive') recorder.stop();
      teardown();
    },
    level() {
      analyser.getFloatTimeDomainData(samples);
      let sum = 0;
      for (const sample of samples) sum += sample * sample;
      const rms = Math.sqrt(sum / samples.length);
      // RMS is tiny for speech; the curve makes normal talking sit mid-meter
      // instead of hugging the floor.
      return Math.min(1, rms * 4);
    },
  };
}

/**
 * Duration of an encoded clip, in ms.
 *
 * `MediaRecorder` output frequently has no duration in its container metadata,
 * so an `<audio>` element reports `Infinity`. Decoding is the reliable answer
 * and is fast enough for narration-length audio.
 */
export async function measureDuration(blob: Blob): Promise<number> {
  try {
    const context = new AudioContext();
    try {
      const buffer = await context.decodeAudioData(await blob.arrayBuffer());
      return Math.round(buffer.duration * 1000);
    } finally {
      void context.close().catch(() => undefined);
    }
  } catch {
    return 0;
  }
}
